import React, { useContext } from 'react';
import { useSnackbar } from 'notistack';

import { PluginContext } from './PluginContext';

export const HttpMethods = {

    GET: 'GET',
    POST: 'POST',
    DELETE: 'DELETE'
}

export const FetchContext = React.createContext(null);

const { Provider } = FetchContext;
export const FetchProvider = ({ ...props }) => {
    const { enqueueSnackbar } = useSnackbar();

    const {
        plugin,
        getApiKeyAsync
    } = useContext(PluginContext);

    const snackOptions = { variant: "error", autoHideDuration: 5000 };

    const createError = (code, message) => {
        const error = new Error(message);
        error.code = code;
        return error;
    }

    const fetchOk = (api, cfg) => {

        var isIE = !!window.MSInputMethodContext && !!document.documentMode;

        const headers = {};
        if (cfg.body && (typeof cfg.body === "string" || cfg.body instanceof String)) {
            headers["Content-Type"] = "application/json"
        }

        if (cfg.headers) {
            for (var pair of cfg.headers.entries()) {
                headers[pair[0]] = pair[1];
            }
        }

        cfg = {
            ...cfg,
            cache: "no-cache",
            mode: "cors",
            headers: new Headers({
                ...headers,
                "Cache-Control": "no-cache",
                "Pragma": "no-cache",
                "Expires": "0",
                "X-Locale": plugin.locale
            })
        };

        return fetch(api, cfg)
            .then(r => {
                if (r.ok) {
                    return r;
                } else {
                    switch (r.status) {
                        // Bad Request
                        case 400:
                            return r.json()
                                .then(data => {
                                    if (data.message) {
                                        return Promise.reject(createError(r.status, data.message));
                                    } else {
                                        if (data.errors) {
                                            const errorKeys = Object.keys(data.errors);
                                            if (cfg.collectErrorDetails) {
                                                const error = new Error("Invalid data supplied");
                                                error.details = errorKeys.map(x => ({ errorId: x, message: data.errors[x][0] }));
                                                error.code = r.status;
                                                return Promise.reject(error);
                                            } else {
                                                errorKeys.forEach(errorKey => {
                                                    const error = data.errors[errorKey][0];
                                                    enqueueSnackbar(error, snackOptions);
                                                });
                                            }
                                        }
                                        return Promise.reject(createError(r.status, "Invalid data supplied"));
                                    }
                                });
                        // Unauthorized
                        case 401:
                            return Promise.reject(createError(r.status, "User is not authorized"));
                        // Forbidden
                        case 403:
                            return Promise.reject(createError(r.status, "User is not allowed"));
                        // Not Found
                        case 404:
                            return Promise.reject(createError(r.status, "Resource not found"));
                        // Server Error
                        case 500:
                            return r.json()
                                .then(data => {
                                    let message = null;
                                    if (data.message) message = data.message;
                                    else message = "An internal error occured";
                                    return Promise.reject(createError(r.status, message));
                                });
                        // Not handled
                        default:
                            return Promise.reject(createError(r.status, "Something went bad"));
                    }
                }
            })
            .catch(error => {
                enqueueSnackbar(error.message, snackOptions);
                return Promise.reject(error);
            });
    }

    // source: https://dev.to/ycmjason/javascript-fetch-retry-upon-failure-3p6g
    const fetchRetry = (retries, api, cfg) => {
        return fetchOk(api, cfg).catch(function (error) {
            if (retries === 1) throw error;
            return fetchRetry(api, cfg, retries - 1);
        });
    }

    const fetchAuthorized = async (api, cfg) => {
        const apiKey = await getApiKeyAsync();
        return fetchOk(api, {
            ...cfg,
            headers: new Headers({
                "MyFastAppToken1": apiKey,
                "X-WP-Nonce": plugin.wordPressNonce,
                "X-Locale": plugin.locale
            })
        })
    }

    const fetchWpAuthorized = async (api, cfg) => {
        return fetchOk(api, {
            ...cfg,
            headers: new Headers({
                "X-WP-Nonce": plugin.wordPressNonce,
                "X-Locale": plugin.locale
            })
        })
    }


    // Used for debug purpose
    const delay = (duration) => {
        return new Promise((resolve) => setTimeout(resolve, duration));
    }

    return (
        <Provider value={{
            fetchRetry,
            fetchOk,
            fetchAuthorized,
            fetchWpAuthorized
        }}>
            {props.children}
        </Provider>
    );
}